import { queryClient } from "../../taskpane";
import { BASE_URL } from "../../auth/auth-api";
import { authenticatedFetch } from "../../auth/fetch";
import {
  AccessLevels,
  Project,
  ProjectFilesResponse,
  ProjectPermissionAction,
  ProjectPermissionResponse,
  ProjectsResponse,
  SupportedTags,
} from "./models";

const PROJECTS_URL = `${BASE_URL}/api/cloud/projects`;
const API_VERSION_HEADER = { "Api-Version": "2023.11.28" };

// NOTE: Use react-query client in favor of ProjectsService at:
//          apps/xltb/toolbox/src/queryclient/projects/projects.ts

// TODO/NOTE: Query client cache is invalidated here; Migrate functions to react-query
export class FileTooLargeError extends Error {}

export default class ProjectsService {
  /**
   * Create Project
   * @param project Project object OR Project name with default values
   * @returns Promise<Response>
   */
  static async createProject(tag: SupportedTags, project: Project | string): Promise<Response> {
    let projectWithMetadata;
    if (typeof project === "string") {
      projectWithMetadata = {
        title: project, // "name" will be auto-generated. Title has fewer naming restrictions.
        metadata: {
          tags: [tag],
          user_client: "excel",
          hidden: false,
        },
      };
    } else {
      projectWithMetadata = {
        ...project,
        metadata: {
          tags: [tag],
          user_client: "excel",
          hidden: false,
        },
      };
    }

    // TODO: what happens if project exists, but lacks specified tag?
    const res = await authenticatedFetch(PROJECTS_URL, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
        ...API_VERSION_HEADER,
      },
      body: JSON.stringify(projectWithMetadata),
    });

    await queryClient.invalidateQueries({ queryKey: ["projects"] });
    return res;
  }

  // Get project by ID
  static async getProjectById(projectId: string): Promise<Response> {
    return await authenticatedFetch(`${PROJECTS_URL}/${projectId}`, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
        ...API_VERSION_HEADER,
      },
    });
  }

  // Get list of projects and files per project;
  // TODO: Sort alphabetically
  // TODO: Handle pagination
  static async getMyProjects(tag?: SupportedTags): Promise<ProjectsResponse> {
    // Use &owner=me to only retrieve projects owned by yourself
    let url = `${PROJECTS_URL}?owner=me`;
    if (tag) {
      url = `${url}&tag=${tag}`;
    }
    const res = await authenticatedFetch(url, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
        ...API_VERSION_HEADER,
      },
    });
    return res.json();
  }

  // Get project files by project ID
  static async getProjectFiles(projectId: string): Promise<ProjectFilesResponse> {
    const res = await authenticatedFetch(`${PROJECTS_URL}/${projectId}/files`, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
        ...API_VERSION_HEADER,
      },
    });
    return res.json();
  }

  // Retrieve all metadata about an individual file in a project
  static async getFileMetadata(projectId: string, fileName: string): Promise<ProjectFilesResponse> {
    const res = await authenticatedFetch(`${PROJECTS_URL}/${projectId}/file-versions/file/${fileName}?latest=True`, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
        ...API_VERSION_HEADER,
      },
    });
    return res.json();
  }

  /**
   * 
   * @param projectId 
   * @param body 
   * Supported metadata changes:
        name
        title
        description
        metadata
        tags
   * @returns Promise<ProjectFilesResponse>
   */
  static async updateProjectMetadata(projectId: string, body: object): Promise<ProjectFilesResponse> {
    const res = await authenticatedFetch(`${PROJECTS_URL}/${projectId}`, {
      method: "PATCH",
      headers: {
        "Content-type": "application/json",
        ...API_VERSION_HEADER,
      },
      body: JSON.stringify(body),
    });

    await queryClient.invalidateQueries({ queryKey: ["projects"], refetchType: "all", exact: true });
    await queryClient.invalidateQueries({ queryKey: [`files-${projectId}`], refetchType: "all", exact: true });

    return res.json();
  }

  // Returns lastModified timestamp
  static async uploadFile(
    projectId: string,
    fileName: string,
    data: string,
    fileOptions: object = null
  ): Promise<string> {
    // Make sure upload isn't too big
    const blob = new Blob([data]);
    const DATA_LIMIT_IN_BYTES = 26214400;

    if (blob.size > DATA_LIMIT_IN_BYTES) {
      console.log("File is too big");
      throw new FileTooLargeError();
    }
    try {
      // Step 1: get redirect URL
      const redirectResponse = await authenticatedFetch(
        `${PROJECTS_URL}/${projectId}/file-preload/${encodeURIComponent(fileName)}`,
        {
          method: "PUT",
          headers: {
            "Content-type": "application/json",
            ...API_VERSION_HEADER,
          },
          body: fileOptions ? JSON.stringify(fileOptions) : "{}",
        }
      );
      const redirectResult = await redirectResponse.json();

      // Step 2: upload content
      const uploadResponse = await fetch(redirectResult.signed_url, {
        method: "PUT",
        headers: { "Content-Length": blob.size.toString() },
        body: blob,
      });

      if (uploadResponse.status >= 300) return null;

      // Step 3: Commit file upload
      await authenticatedFetch(`${PROJECTS_URL}/${projectId}/file-versions/${redirectResult.file_version_id}`, {
        method: "PATCH",
        headers: {
          "Content-type": "application/json",
          ...API_VERSION_HEADER,
        },
      });

      await queryClient.invalidateQueries({ queryKey: [`files-${projectId}`], refetchType: "all", exact: true });

      return redirectResult.last_modified;
    } catch (e) {
      console.error(e);
    }
  }

  // Upload CSV file to project by ID
  static async uploadCSV(projectId: string, fileName: string, csvString: string): Promise<string> {
    return await this.uploadFile(projectId, fileName, csvString);
  }

  // Upload code snippet
  static async uploadSnippet(projectId: string, fileName: string, code: string, language: string): Promise<string> {
    return await this.uploadFile(projectId, fileName, code, { metadata: { code_snippet: { language } } });
  }

  /**
   * Download project file by file name in project ID
   * @param0 projectId
   * @param1 fileName
   * @returns Promise<Response>; Use <Response>.text() to get the CSV file contents
   */
  static async downloadFileByName(projectId: string, fileName: string): Promise<Response> {
    const res = await authenticatedFetch(`${PROJECTS_URL}/${projectId}/files/${fileName}`, {
      method: "GET",
      headers: API_VERSION_HEADER,
    });
    await queryClient.invalidateQueries({ queryKey: [`files-${projectId}`], refetchType: "all", exact: true });
    return res;
  }

  // Delete project by ID
  static async deleteProjectById(projectId: string): Promise<Response> {
    return await authenticatedFetch(`${PROJECTS_URL}/${projectId}`, {
      method: "DELETE",
      headers: API_VERSION_HEADER,
    });
  }

  // Delete file by name
  static async deleteFileByName(projectId: string, fileName: string): Promise<Response> {
    // console.log(queryClient.getQueryCache().find({ queryKey: [`files-${projectId}`] }));
    const res = await authenticatedFetch(`${PROJECTS_URL}/${projectId}/files/${fileName}`, {
      method: "DELETE",
      headers: API_VERSION_HEADER,
    });
    await queryClient.invalidateQueries({ queryKey: [`files-${projectId}`], refetchType: "all", exact: true });
    return res;
  }

  // Get permissions to read or write data in projects, which can be granted to individual users or organizations
  static async getProjectPermissions(projectId: string): Promise<ProjectPermissionResponse> {
    const res = await authenticatedFetch(`${PROJECTS_URL}/${projectId}/permissions`, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
        ...API_VERSION_HEADER,
      },
    });
    return res.json();
  }

  // Get user permissions for a projects
  static async getMyProjectAccessLevels(projectId: string): Promise<AccessLevels> {
    const res = await authenticatedFetch(`${PROJECTS_URL}/${projectId}/permissions/my`, {
      method: "GET",
      headers: {
        "Content-type": "application/json",
        ...API_VERSION_HEADER,
      },
    });
    return res.json();
  }

  /**
   * Modify/grant permissions by supplying a list of actions to add or remove entries from the permissions output.
   * Note that only an owner can modify permissions.
   */
  static async modifyProjectPermissions(
    projectId: string,
    permissions: ProjectPermissionAction[]
  ): Promise<ProjectPermissionResponse> {
    const res = await authenticatedFetch(`${PROJECTS_URL}/${projectId}/permissions`, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
        ...API_VERSION_HEADER,
      },
      body: JSON.stringify({ permissions }),
    });
    return res.json();
  }
}
